/**
 * Apps a client adds to one tier of their pyramid with the tier's "+" button
 * (TechStack.js, also CRM Portal View) — anything that isn't a template tool.
 *
 * Each one is just an entry in accounts/{id}.techStack.selectedSoftware with
 * its own tool slot, so it saves, counts and syncs like any other pick:
 *   { id, name, image, signupUrl, open, useCaseIds: [], suggested: false,
 *     categoryId, toolId: 'added-…', toolName, addedApp: true,
 *     baseToolId, isCustom (true when it isn't from the template library), selectedAt }
 * toolName is the label under the icon (the tool type). baseToolId is the
 * template tool type it was added to (a second AI next to AI), or null for a
 * tool type the client typed in. Mirrored in functions/mcp/techStack.js.
 */

export const isAddedApp = (selection) => !!selection?.addedApp && String(selection.toolId || '').startsWith('added-');

export const newAddedAppToolId = () => `added-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Pyramid tools for a tier's added apps, shaped like template tools. */
export const addedAppTools = (selections, categoryId) =>
  (selections || [])
    .filter((s) => isAddedApp(s) && s.categoryId === categoryId)
    .sort((a, b) => String(a.selectedAt || '').localeCompare(String(b.selectedAt || '')))
    .map((s) => ({
      id: s.toolId,
      name: s.toolName || 'Other app',
      software: s.name,
      description: '',
      useCases: [],
      availableSoftware: [
        { id: s.id, name: s.name, image: s.image || '', signupUrl: s.signupUrl || '', open: !!s.open, useCaseIds: [], suggested: false },
      ],
      proposedSoftware: '',
      customSoftwareOpen: !!s.open,
      addedApp: true,
      baseToolId: s.baseToolId || null,
    }));

/** A tier's tools with each added app placed right after its template tool type; new tool types go last. */
export const withAddedApps = (templateTools, selections, categoryId) => {
  const added = addedAppTools(selections, categoryId);
  const templateIds = new Set(templateTools.map((tool) => tool.id));
  return [
    ...templateTools.flatMap((tool) => [tool, ...added.filter((a) => a.baseToolId === tool.id)]),
    ...added.filter((a) => !templateIds.has(a.baseToolId)),
  ];
};

/** The selection entry saved for an added app. `library` is the template software it came from, if any. */
export const buildAddedAppSelection = ({ categoryId, toolId, name, toolName, baseToolId = null, open, library = null }) => ({
  id: library?.id || `custom-${toolId}`,
  name: name.trim(),
  image: library?.image || '',
  signupUrl: library?.signupUrl || '',
  open: !!open,
  useCaseIds: [],
  suggested: false,
  categoryId,
  toolId,
  toolName: toolName.trim(),
  addedApp: true,
  baseToolId: baseToolId || null,
  isCustom: !library,
  selectedAt: new Date().toISOString(),
});

/**
 * A tier's template tool types for the "+" picker: each type's own software
 * (not "Your X" Core links) and the app names already in the stack for it —
 * the type's pick plus apps added beside it.
 */
export const tierToolTypes = (category) => {
  const tools = category?.tools || [];
  return tools
    .filter((tool) => !tool.addedApp)
    .map((tool) => ({
      id: tool.id,
      name: tool.name,
      software: (tool.availableSoftware || []).filter((sw) => sw.name && !sw.fromCore),
      inStack: [tool.software, ...tools.filter((t) => t.addedApp && t.baseToolId === tool.id).map((t) => t.software)].filter(
        (n) => n && n !== 'None'
      ),
    }));
};
